import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, CheckCircle, AlertCircle, User, MessageSquare, Building } from 'lucide-react';

const ContactForm = ({ 
  title = "Get in Touch",
  subtitle = "Let's discuss how EMBER can support your leadership journey",
  className = "",
  inquiry = "" // Pre-filled inquiry type
}) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    inquiryType: inquiry || '',
    message: ''
  });
  const [status, setStatus] = useState('idle'); // idle, submitting, success, error
  const [message, setMessage] = useState('');

  const inquiryTypes = [
    { value: 'speaking', label: 'Speaking Engagement' },
    { value: 'consulting', label: 'Consulting Services' },
    { value: 'partnerships', label: 'Partnership Opportunities' },
    { value: 'custom', label: 'Custom Program Development' },
    { value: 'general', label: 'General Inquiry' }
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error status when user starts typing after an error
    if (status === 'error') {
      setStatus('idle');
      setMessage('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.message) {
      setStatus('error');
      setMessage('Please fill in all required fields');
      return;
    }

    if (!formData.email.includes('@')) {
      setStatus('error');
      setMessage('Please enter a valid email address');
      return;
    }

    setStatus('submitting');

    try {
      // Enhanced FormSubmit.co integration for cross-domain reliability
      const emailFormData = new FormData();
      
      // Essential fields
      emailFormData.append('name', formData.name);
      emailFormData.append('email', formData.email);
      emailFormData.append('company', formData.company || 'Not provided');
      emailFormData.append('inquiry_type', formData.inquiryType || 'General Inquiry');
      emailFormData.append('message', formData.message);
      
      // FormSubmit configuration for reliability
      emailFormData.append('_subject', `EMBER Contact Form - ${formData.name}`);
      emailFormData.append('_template', 'table');
      emailFormData.append('_captcha', 'false');
      emailFormData.append('_autoresponse', 'Thank you for contacting EMBER. We will respond within 24 hours.');
      
      // Domain-specific configuration
      const currentDomain = window.location.hostname;
      if (currentDomain === 'emberimpact.com' || currentDomain === 'www.emberimpact.com') {
        emailFormData.append('_next', 'https://emberimpact.com/contact?success=true');
      } else {
        emailFormData.append('_next', `${window.location.origin}/contact?success=true`);
      }

      console.log(`Sending email from domain: ${currentDomain} to matt@emberimpact.com`);

      // Primary email sending method - FormSubmit.co
      const emailResponse = await fetch('https://formsubmit.co/matt@emberimpact.com', {
        method: 'POST',
        body: emailFormData,
        mode: 'no-cors' // Important for cross-domain reliability
      });

      console.log('Email submission completed via FormSubmit.co');

      // Also log to backend for tracking (optional, non-blocking)
      try {
        const trackingData = {
          name: formData.name,
          email: formData.email,
          company: formData.company || null,
          inquiryType: formData.inquiryType || null,
          message: formData.message,
          domain: currentDomain,
          timestamp: new Date().toISOString()
        };

        // Try backend logging but don't fail if it doesn't work
        const backendUrl = process.env.REACT_APP_BACKEND_URL;
        
        await fetch(`${backendUrl}/api/contact`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(trackingData)
        });

        console.log('Form submission logged to backend');
      } catch (backendError) {
        console.warn('Backend logging failed (email still sent):', backendError);
      }

      // Success - FormSubmit.co is very reliable with mode: 'no-cors'
      setStatus('success');
      setMessage('Thank you! Your message has been sent to matt@emberimpact.com. We\'ll get back to you within 24 hours.');
      
      setFormData({
        name: '',
        email: '',
        company: '',
        inquiryType: inquiry || '',
        message: ''
      });

    } catch (error) {
      console.error('Primary email method failed:', error);
      
      // Fallback method with different configuration
      try {
        console.log('Attempting fallback email delivery...');
        
        // Alternative FormSubmit approach with minimal configuration
        const fallbackData = new FormData();
        fallbackData.append('name', formData.name);
        fallbackData.append('email', formData.email);
        fallbackData.append('message', `
EMBER Website Contact Form Submission

Name: ${formData.name}
Email: ${formData.email}
Company: ${formData.company || 'Not provided'}
Inquiry Type: ${formData.inquiryType || 'General Inquiry'}
Submitted from: ${window.location.hostname}

Message:
${formData.message}

---
Sent via EMBER contact form fallback system
        `);
        fallbackData.append('_subject', `EMBER Contact - ${formData.name} (Fallback)`);

        // Use a different endpoint approach for fallback
        await fetch('https://formsubmit.co/matt@emberimpact.com', {
          method: 'POST',
          body: fallbackData,
          mode: 'no-cors'
        });

        setStatus('success');
        setMessage('Thank you! Your message has been sent to matt@emberimpact.com via our backup system. We\'ll get back to you within 24 hours.');
        
        setFormData({
          name: '',
          email: '',
          company: '',
          inquiryType: inquiry || '',
          message: ''
        });

      } catch (fallbackError) {
        console.error('All email methods failed:', fallbackError);
        setStatus('error');
        setMessage('We\'re experiencing technical difficulties. Please email us directly at matt@emberimpact.com or call 415-347-6475.');
      }
    }
  };

  const resetStatus = () => {
    setStatus('idle');
    setMessage('');
  };

  return (
    <motion.div
      className={`bg-white rounded-3xl p-8 shadow-xl border border-neutral-light max-w-4xl mx-auto ${className}`}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      viewport={{ once: true }}
    >
      <div className="text-center mb-8">
        <Mail className="w-12 h-12 mx-auto mb-4 text-ember-fire" />
        <h3 className="text-3xl font-bold mb-2 text-neutral-charcoal">{title}</h3>
        <p className="text-neutral-medium text-lg">{subtitle}</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid md:grid-cols-2 gap-6">
          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-neutral-charcoal mb-2">
              <User className="w-4 h-4 inline mr-2" />
              Full Name *
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              placeholder="Your full name"
              className="w-full px-4 py-3 rounded-xl border border-neutral-light focus:outline-none focus:border-ember-fire focus:ring-2 focus:ring-ember-fire/20 transition-colors"
              required
              disabled={status === 'submitting'}
            />
          </div>

          {/* Email */}
          <div>
            <label className="block text-sm font-medium text-neutral-charcoal mb-2">
              <Mail className="w-4 h-4 inline mr-2" />
              Email Address *
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              placeholder="your@email.com"
              className="w-full px-4 py-3 rounded-xl border border-neutral-light focus:outline-none focus:border-ember-fire focus:ring-2 focus:ring-ember-fire/20 transition-colors"
              required
              disabled={status === 'submitting'}
            />
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Company */}
          <div>
            <label className="block text-sm font-medium text-neutral-charcoal mb-2">
              <Building className="w-4 h-4 inline mr-2" />
              Company/Organization
            </label>
            <input
              type="text"
              name="company"
              value={formData.company}
              onChange={handleInputChange}
              placeholder="Your company name"
              className="w-full px-4 py-3 rounded-xl border border-neutral-light focus:outline-none focus:border-ember-fire focus:ring-2 focus:ring-ember-fire/20 transition-colors"
              disabled={status === 'submitting'}
            />
          </div>

          {/* Inquiry Type */}
          <div>
            <label className="block text-sm font-medium text-neutral-charcoal mb-2">
              Inquiry Type
            </label>
            <select
              name="inquiryType"
              value={formData.inquiryType}
              onChange={handleInputChange}
              className="w-full px-4 py-3 rounded-xl border border-neutral-light focus:outline-none focus:border-ember-fire focus:ring-2 focus:ring-ember-fire/20 transition-colors"
              disabled={status === 'submitting'}
            >
              <option value="">Select an option</option>
              {inquiryTypes.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Message */}
        <div>
          <label className="block text-sm font-medium text-neutral-charcoal mb-2">
            <MessageSquare className="w-4 h-4 inline mr-2" />
            Message *
          </label>
          <textarea
            name="message"
            value={formData.message}
            onChange={handleInputChange}
            placeholder="Tell us about your needs, goals, or how we can help..."
            rows={6}
            className="w-full px-4 py-3 rounded-xl border border-neutral-light focus:outline-none focus:border-ember-fire focus:ring-2 focus:ring-ember-fire/20 transition-colors resize-none"
            required
            disabled={status === 'submitting'}
          />
        </div>
        
        <motion.button
          type="submit"
          disabled={status === 'submitting' || status === 'success'}
          className="w-full bg-ember-gradient text-white px-8 py-4 rounded-xl font-bold text-lg hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          whileHover={{ scale: status === 'idle' ? 1.02 : 1 }}
          whileTap={{ scale: status === 'idle' ? 0.98 : 1 }}
        >
          {status === 'submitting' ? 'Sending Message...' : 
           status === 'success' ? 'Message Sent!' : 'Send Message'}
        </motion.button>

        {message && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className={`flex items-center justify-center gap-2 text-sm ${
              status === 'error' ? 'text-red-600' : 'text-green-600'
            }`}
          >
            {status === 'error' ? <AlertCircle className="w-4 h-4" /> : <CheckCircle className="w-4 h-4" />}
            {message}
            {status === 'error' && (
              <button
                type="button"
                onClick={resetStatus}
                className="ml-2 underline hover:no-underline"
              >
                Try again
              </button>
            )}
          </motion.div>
        )}
      </form>

      <div className="mt-6 pt-6 border-t border-neutral-light text-center">
        <p className="text-xs text-neutral-medium">
          We typically respond within 24 hours during business days.
        </p>
      </div>
    </motion.div>
  );
};

export default ContactForm;